import { alpha } from '@mui/material/styles';
import { getDrawerDatePickerSlotProps } from '../../../utils/drawerOverlayProps';

export const HALL_FORM_ACCENT = '#4ce1b6';
export const HALL_SIDE_DRAWER_WIDTH_SX = { width: { xs: '100%', sm: 600 }, maxWidth: '100vw', boxSizing: 'border-box' };
export const HALL_DRAWER_FIELD_GAP = 2;

export function getHallDrawerPaperSx(isDark) {
  return {
    ...HALL_SIDE_DRAWER_WIDTH_SX,
    display: 'flex',
    flexDirection: 'column',
    bgcolor: isDark ? '#111827' : '#ffffff',
    color: isDark ? '#f8fafc' : '#0f172a',
  };
}

/** Soft filled inputs — matches housekeeping / maintenance drawer fields. */
export function getHallFormInputSx(isDark) {
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      color: isDark ? '#f8fafc' : '#222',
      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
      transition: 'background-color 0.2s ease',
      minHeight: 48,
      '& fieldset': {
        border: 'none',
        borderWidth: 0,
      },
      '&:hover fieldset': {
        border: 'none',
        borderWidth: 0,
      },
      '&.Mui-focused fieldset': {
        border: 'none',
        borderWidth: 0,
      },
      '&.Mui-error fieldset': {
        border: 'none',
        borderWidth: 0,
      },
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.055)',
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.055)',
        boxShadow: 'none',
      },
      '&.Mui-error': {
        backgroundColor: isDark ? 'rgba(244,67,54,0.12)' : 'rgba(244,67,54,0.08)',
        boxShadow: 'none',
      },
      '&.Mui-disabled': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderWidth: 0,
    },
    '& .MuiInputLabel-root': {
      color: isDark ? '#8a939e' : '#78828c',
      '&.Mui-focused': { color: isDark ? '#8a939e' : '#78828c' },
      '&.Mui-error': { color: '#f44336' },
    },
    '& .MuiFormLabel-asterisk': {
      color: 'inherit',
    },
    '& .MuiFormHelperText-root': {
      color: isDark ? '#8a939e' : '#78828c',
      mx: 0.5,
    },
    '& .MuiInputAdornment-root, & .MuiSvgIcon-root': {
      color: isDark ? '#8a939e' : '#78828c',
    },
  };
}

export function getHallFormSectionTitleSx() {
  return {
    color: HALL_FORM_ACCENT,
    fontWeight: 900,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontSize: '0.74rem',
  };
}

export function getHallDrawerFormGridSx(isDark) {
  return {
    width: '100%',
    maxWidth: '100%',
    m: 0,
    alignItems: 'stretch',
    ...getHallFormInputSx(isDark),
    '& .MuiInputBase-root': {
      minHeight: 48,
    },
    '& .MuiInputBase-input': {
      fontSize: '0.9rem',
    },
  };
}

/** Date / DateTime / Time picker slotProps — soft field + drawer-safe popover (matches maintenance). */
export function getHallDatePickerSlotProps(isDark, textField = {}) {
  const paperBg = isDark ? '#1a1e28' : '#ffffff';
  const overlay = getDrawerDatePickerSlotProps(isDark, paperBg);
  const { sx: textFieldSx, ...textFieldRest } = textField;

  return {
    ...overlay,
    textField: {
      size: 'small',
      fullWidth: true,
      ...textFieldRest,
      sx: {
        ...getHallFormInputSx(isDark),
        ...textFieldSx,
      },
    },
    desktopPaper: {
      sx: {
        ...overlay.desktopPaper?.sx,
        borderRadius: '16px',
        border: 'none',
        overflow: 'hidden',
        bgcolor: paperBg,
        backgroundImage: 'none',
        color: isDark ? '#e8edf2' : '#222',
        boxShadow: isDark
          ? '0 16px 40px rgba(0,0,0,0.5)'
          : '0 12px 32px rgba(0,0,0,0.12)',
        '& .MuiPickersLayout-root': {
          bgcolor: paperBg,
        },
        '& .MuiDateCalendar-root, & .MuiDayCalendar-root': {
          bgcolor: paperBg,
        },
        '& .MuiMultiSectionDigitalClock-root': {
          bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          borderLeft: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
        },
        '& .MuiMultiSectionDigitalClockSection-root': {
          borderLeftColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.16)',
            borderRadius: 4,
          },
        },
        '& .MuiMultiSectionDigitalClockSection-item': {
          borderRadius: '10px',
          margin: '2px 4px',
          fontWeight: 600,
          '&.Mui-selected': {
            backgroundColor: `${HALL_FORM_ACCENT} !important`,
            color: '#031512 !important',
            fontWeight: 800,
          },
          '&:hover': {
            backgroundColor: alpha(HALL_FORM_ACCENT, isDark ? 0.16 : 0.1),
          },
        },
        '& .MuiPickersDay-root': {
          borderRadius: '50%',
          fontWeight: 600,
          '&.Mui-selected': {
            backgroundColor: `${HALL_FORM_ACCENT} !important`,
            color: '#031512 !important',
            fontWeight: 800,
          },
          '&.MuiPickersDay-today:not(.Mui-selected)': {
            borderColor: isDark ? 'rgba(255,255,255,0.45)' : alpha(HALL_FORM_ACCENT, 0.55),
          },
        },
        '& .MuiPickersCalendarHeader-label, & .MuiDayCalendar-weekDayLabel': {
          color: isDark ? '#aab2bc' : '#5a646e',
          fontWeight: 700,
        },
        '& .MuiPickersArrowSwitcher-button, & .MuiIconButton-root': {
          color: isDark ? '#aab2bc' : '#5a646e',
        },
        '& .MuiDialogActions-root': {
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          px: 1.5,
          py: 1,
        },
        '& .MuiDialogActions-root .MuiButton-root': {
          color: HALL_FORM_ACCENT,
          fontWeight: 800,
          borderRadius: '10px',
          px: 1.5,
        },
      },
    },
  };
}
